import { action, computed, observable } from 'mobx'
import {
  type WorkspaceApplicant,
  type WorkspaceApplicantStatus,
} from '@/features/admin/staffing/WorkspaceApplicant'
import { type WorkspacePosting } from '@/features/admin/staffing/WorkspacePosting'
import { type WorkspacePostingStore } from '@/features/admin/staffing/WorkspacePostingStore'
import { csvDownload } from '@/utils/csv-util'
import { ApplicantManagementTableColumns } from '@/features/admin/staffing/components/ApplicantManagementTable/applicant-management-table-columns'
import type { SelectionState } from '@/infra/selection-state'
import type {
  ProviderPtinActiveStatus,
  ProviderStatus,
} from '@/features/iam/providers/Provider'
import { match } from '@/utils/pattern-match-util'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { getProviderTypeTitle } from '@/features/iam/providers/provider-display'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { WorkspaceApplicantItemViewModel } from './WorkspaceApplicantItemViewModel'

export class ApplicantManagementPanelViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _items = new ViewModelList({
    models: () => this.filteredApplicants.slice(),
    create: (applicant: WorkspaceApplicant) =>
      new WorkspaceApplicantItemViewModel(applicant, this),
  })

  readonly modal = new ModalViewModel({ onClose: () => this._reset() })

  /**
   * Columns to populate the applicant management table header and rows with.
   */
  readonly columns = ApplicantManagementTableColumns

  /**
   * Search text, filtering the posting's applicants.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Currently selected applicants.
   */
  @observable accessor selectedApplicants = new Set<WorkspaceApplicant>()

  constructor(
    private readonly _workspacePostingStore: WorkspacePostingStore,
    private readonly _getWorkspacePosting: () => WorkspacePosting | null,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(this._items)
  }

  /**
   * The selected workspace posting.
   */
  @computed
  get workspacePosting() {
    return this._getWorkspacePosting()
  }

  /**
   * The remaining capacity of the selected workspace posting.
   */
  @computed
  get remainingCapacity() {
    if (this.workspacePosting === null) {
      return 0
    }

    const capacity = this.workspacePosting.capacity ?? 0
    const invitedOrAcceptedApplicants =
      this.workspacePosting.applicants.filter(
        (a) => a.status === 'Accepted' || a.status === 'Invited',
      ).length ?? 0

    return capacity - invitedOrAcceptedApplicants
  }

  /**
   * The filtered set of applicants for the selected workspace posting.
   */
  @computed
  get filteredApplicants() {
    const applicants = this.workspacePosting?.applicants.items ?? []

    if (!this.searchText.value) {
      return applicants
    }

    const text = this.searchText.value.toLowerCase()
    return applicants.filter(
      (a) =>
        a.displayName.toLocaleLowerCase().includes(text) ||
        a.provider.stateRegion.toLowerCase().includes(text) ||
        a.status.toLowerCase().includes(text),
    )
  }

  /**
   * The table rows.
   */
  get items() {
    return this._items.items
  }

  /**
   * Whether the selected applicants can be invited without exceeding the
   * posting's capacity.
   */
  @computed
  get canInviteSelected() {
    return (
      this.selectedApplicants.size >= 1 &&
      this.selectedApplicants.size <= this.remainingCapacity
    )
  }

  /**
   * Returns whether applicant is currently selected.
   * @param applicant
   */
  isSelected(applicant: WorkspaceApplicant): boolean {
    return this.selectedApplicants.has(applicant)
  }

  /**
   * Returns whether the applicant can be invited to the posting.
   * @param applicant
   */
  canInvite(applicant: WorkspaceApplicant): boolean {
    return (
      applicant.status !== 'Invited' &&
      applicant.status !== 'Accepted' &&
      this.remainingCapacity >= 1
    )
  }

  /**
   * Returns whether the applicant's invitation can be rescinded.
   * @param applicant
   */
  canRescindInvitation(applicant: WorkspaceApplicant): boolean {
    return applicant.status === 'Invited'
  }

  /**
   * Returns whether applicants are all selected, none selected, or in between.
   */
  @computed
  get selectionState(): SelectionState {
    if (this.selectedApplicants.size === 0) {
      return 'NoneSelected'
    }

    if (this.filteredApplicants.length === this.selectedApplicants.size) {
      return 'AllSelected'
    }

    if (this.selectedApplicants.size > 0) {
      return 'SomeSelected'
    }

    return 'NoneSelected'
  }

  /**
   * Toggles whether an individual applicant is selected.
   * @param applicant
   */
  @action.bound
  toggleApplicant(applicant: WorkspaceApplicant) {
    if (this.isSelected(applicant)) {
      this.selectedApplicants.delete(applicant)
    } else {
      this.selectedApplicants.add(applicant)
    }
  }

  /**
   * Toggles whether all applicants are selected.
   */
  @action.bound
  toggleAllApplicants() {
    switch (this.selectionState) {
      case 'NoneSelected':
      case 'SomeSelected':
        this.filteredApplicants.forEach((applicant) => {
          this.selectedApplicants.add(applicant)
        })
        break
      case 'AllSelected':
        this.clearSelected()
        break
    }
  }

  /**
   * Removes all selected applicants.
   */
  @action.bound
  clearSelected() {
    this.selectedApplicants.clear()
  }

  /**
   * Invites an applicant.
   * @param applicant
   */
  @action.bound
  async inviteApplicant(applicant: WorkspaceApplicant) {
    if (this.workspacePosting === null || this.remainingCapacity < 1) {
      return
    }
    const postingId = this.workspacePosting.id

    await this._workspacePostingStore
      .inviteApplicant(postingId, applicant.userPublicId)
      .catch(this._createAlertErrorHandler('Inviting the applicant failed'))
    await this._refetchWorkspacePosting(postingId)
  }

  /**
   * Rescinds an invitation.
   * @param applicant
   */
  @action.bound
  async rescindInvitation(applicant: WorkspaceApplicant) {
    if (this.workspacePosting === null) {
      return
    }
    const postingId = this.workspacePosting.id

    await this._workspacePostingStore
      .rescindInvitation(postingId, applicant.userPublicId)
      .catch(this._createAlertErrorHandler('Rescinding the invitation failed'))
    await this._refetchWorkspacePosting(postingId)
  }

  /**
   * Invites the selected applicants.
   */
  @action.bound
  async inviteSelected() {
    if (
      this.workspacePosting === null ||
      this.selectedApplicants.size === 0 ||
      this.remainingCapacity < 1
    ) {
      return
    }
    const postingId = this.workspacePosting.id

    await Promise.all(
      Array.from(this.selectedApplicants).map((applicant) =>
        this._workspacePostingStore.inviteApplicant(
          postingId,
          applicant.userPublicId,
        ),
      ),
    ).catch(this._createAlertErrorHandler('Inviting the applicants failed'))
    await this._refetchWorkspacePosting(postingId)
  }

  /**
   * Rescinds the invitation for the selected applicants.
   */
  @action.bound
  async rescindInvitationForSelected() {
    const invitedApplicants = [...this.selectedApplicants].filter(
      (a) => a.status === 'Invited',
    )
    if (this.workspacePosting === null || invitedApplicants.length === 0) {
      return
    }

    const postingId = this.workspacePosting.id

    await Promise.all(
      invitedApplicants.map((applicant) =>
        this._workspacePostingStore.rescindInvitation(
          postingId,
          applicant.userPublicId,
        ),
      ),
    ).catch(this._createAlertErrorHandler('Rescinding the invitations failed'))
    await this._refetchWorkspacePosting(postingId)
  }

  /**
   * Exports applicants as a CSV file.
   * If applicants are selected, exports only those; otherwise exports all filtered applicants.
   */
  @action.bound
  exportApplicantsAsCSV() {
    const applicants =
      this.selectedApplicants.size > 0
        ? Array.from(this.selectedApplicants)
        : this.filteredApplicants

    if (applicants.length === 0) {
      return
    }

    const header = [
      'Applicant Name',
      'Contact Owner',
      'Type',
      'Location',
      'Experience',
      'Total Workspaces',
      'Global Capacity',
      'Status',
      'Email',
      'Phone',
      'Software Experience',
      'Software Owned',
      'Supplemental Software',
      'PTIN Status',
      'Provider Status',
      'Specialties',
      'Date Created',
      'Date Modified',
    ]

    const data = applicants.map((applicant) => {
      const capacity = applicant.provider.globalJobCapacity
      return [
        applicant.displayName,
        applicant.provider.proManager,
        getProviderTypeTitle(applicant.provider.proType),
        applicant.provider.stateRegion,
        applicant.provider.yearsExperience.toString(),
        applicant.provider.workspaces.length.toString(),
        capacity > 0 ? capacity.toString() : 'Unlimited',
        formatApplicantStatusForExport(applicant.status),
        applicant.provider.email,
        applicant.provider.phone,
        applicant.provider.softwareExperience.join(', '),
        applicant.provider.softwareOwned.join(', '),
        applicant.provider.supplementalSoftwareExperience.join(', '),
        formatPtinStatusForExport(applicant.provider.activePtin),
        formatProviderStatusForExport(applicant.provider.providerStatus),
        applicant.provider.specialties.join(', '),
        applicant.dateCreated?.toLocaleString(),
        applicant.dateModified?.toLocaleString(),
      ]
    })

    const name =
      this.workspacePosting?.workspace?.name
        ?.replace(/[^a-zA-Z0-9 ]/g, '')
        .trim() || 'applicants'
    const filename = `applicants-${name}.csv`

    csvDownload([header, ...data], filename)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _refetchWorkspacePosting(id: Id) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading the posting failed',
    )
    // The write succeeded; a dismissed refresh leaves the panel as it was.
    await withRetry(() =>
      this._workspacePostingStore.fetchWorkspacePosting(id),
    ).catch(() => undefined)
  }

  @action.bound
  private _reset() {
    this.searchText.clear()
    this.clearSelected()
  }
}

/**
 * Exported files keep the status codes the API uses.
 */
function formatApplicantStatusForExport(status: WorkspaceApplicantStatus) {
  return match(status, {
    Applied: () => 'APPLIED',
    Rejected: () => 'REJECTED',
    Invited: () => 'INVITED',
    Accepted: () => 'ACCEPTED',
    Withdrawn: () => 'WITHDRAWN',
    Expired: () => 'EXPIRED',
    Null: () => 'NULL',
  })
}

function formatPtinStatusForExport(status: ProviderPtinActiveStatus) {
  return match(status, {
    Active: () => 'ACTIVE',
    Inactive: () => 'INACTIVE',
    NotApplicable: () => 'NOT_APPLICABLE',
  })
}

function formatProviderStatusForExport(status: ProviderStatus) {
  return match(status, {
    Active: () => 'ACTIVE',
    Decommissioned: () => 'DECOMMISSIONED',
    Suspended: () => 'SUSPENDED',
    OnLeave: () => 'ON_LEAVE',
    Null: () => 'NULL',
  })
}
